import React, { useState } from 'react';
import { Plus, Trash2, X } from 'lucide-react';
import { COR, FONTE } from './ui';
import { CAMADA, authHeaders, MESES, modalOverlay, modalCard, modalHeader, btnX, rotuloStyle, inputStyle } from './calendario-ui';

// =====================================================================
// PAINEL DE FERIADOS — cadastrar + remover os feriados do mês
// =====================================================================
// Morava dentro do CalendarioAniversariantes (como "GestaoFeriados"); saiu de lá
// quando o modal do ícone de calendário passou a cadastrar feriado também. Os
// dois montam este mesmo painel — é form E lista de uma vez, porque cadastrar
// feriado quase sempre vem junto de conferir o que já está lá.
//
// ⚠️ SÓ Admin e Marketing. O POST/DELETE do backend tem authorizeRoles
// (server/home/server-calendario.js › PERFIS_GESTAO) — quem chama deve esconder
// o acesso para os demais perfis (ver PERFIS_GESTAO_FERIADO em calendario-ui),
// senão o usuário abre o painel e leva 403 ao salvar.
//
// Feriado vale para a REDE TODA — não tem compartilhamento por perfil/loja como
// o evento. O recorte é geográfico: tipo federal/estadual/municipal (+ UF e
// município), que é o que diz a quais lojas ele se aplica de fato.
//
// Props:
//   - ano, mes   : mês exibido (a data padrão do form cai no dia 1 dele)
//   - feriados   : os feriados JÁ carregados do mês (vêm do useCalendarioMes)
//   - onFechar   : fecha o painel
//   - onMudou    : cadastrou ou removeu (quem chama recarrega)

function PainelFeriados({ ano, mes, feriados, onFechar, onMudou }) {
    const dataPadrao = `${ano}-${String(mes).padStart(2, '0')}-01`;
    const [form, setForm] = useState({ data: dataPadrao, nome: '', tipo: 'federal', uf: '', municipio: '' });
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState('');

    const set = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

    const adicionar = async () => {
        setErro('');
        if (!form.nome.trim()) { setErro('Informe o nome do feriado.'); return; }
        setSalvando(true);
        try {
            const r = await fetch('/api/calendario/feriados', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify(form),
            });
            if (r.ok) {
                setForm({ data: dataPadrao, nome: '', tipo: 'federal', uf: '', municipio: '' });
                onMudou();
            } else {
                const b = await r.json().catch(() => ({}));
                setErro(b.message || 'Erro ao cadastrar.');
            }
        } catch (e) {
            setErro('Erro de conexão.');
        } finally {
            setSalvando(false);
        }
    };

    const remover = async (id) => {
        try {
            const r = await fetch(`/api/calendario/feriados/${id}`, { method: 'DELETE', headers: authHeaders() });
            if (r.ok) onMudou();
        } catch (e) { /* ignora */ }
    };

    return (
        <div onClick={onFechar} style={modalOverlay}>
            <div onClick={(e) => e.stopPropagation()} style={modalCard}>
                {/* Cabeçalho */}
                <div style={modalHeader}>
                    <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 700, color: COR.texto }}>Feriados de {MESES[mes - 1]} {ano}</h3>
                    <button onClick={onFechar} style={btnX}><X size={20} /></button>
                </div>

                {/* Formulário de cadastro */}
                <div style={{ padding: '18px', display: 'flex', flexDirection: 'column', gap: '10px', borderBottom: `1px solid ${COR.bordaSuave}` }}>
                    <label style={rotuloStyle}>Data
                        <input type="date" value={form.data} onChange={set('data')} style={inputStyle} />
                    </label>
                    <label style={rotuloStyle}>Nome
                        <input type="text" value={form.nome} onChange={set('nome')} placeholder="Ex.: Dia do Trabalhador" style={inputStyle} />
                    </label>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <label style={{ ...rotuloStyle, flex: 1 }}>Tipo
                            <select value={form.tipo} onChange={set('tipo')} style={inputStyle}>
                                <option value="federal">Federal</option>
                                <option value="estadual">Estadual</option>
                                <option value="municipal">Municipal</option>
                            </select>
                        </label>
                        <label style={{ ...rotuloStyle, width: '80px' }}>UF
                            <input type="text" maxLength={2} value={form.uf} onChange={set('uf')} placeholder="ES" style={inputStyle} />
                        </label>
                    </div>
                    {form.tipo === 'municipal' && (
                        <label style={rotuloStyle}>Município
                            <input type="text" value={form.municipio} onChange={set('municipio')} placeholder="Ex.: Linhares" style={inputStyle} />
                        </label>
                    )}
                    {erro && <div style={{ color: '#dc2626', fontSize: '12px' }}>{erro}</div>}
                    <button onClick={adicionar} disabled={salvando} style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: CAMADA.feriado.cor, color: '#fff', border: 'none', borderRadius: '8px', padding: '9px 16px', fontFamily: FONTE, fontWeight: 700, fontSize: '13px', cursor: salvando ? 'default' : 'pointer', opacity: salvando ? 0.6 : 1 }}>
                        <Plus size={16} /> {salvando ? 'Salvando…' : 'Adicionar feriado'}
                    </button>
                </div>

                {/* Lista do mês */}
                <div style={{ padding: '14px 18px' }}>
                    {feriados.length === 0 ? (
                        <div style={{ color: '#999', fontSize: '13px', textAlign: 'center', padding: '12px 0' }}>Nenhum feriado cadastrado neste mês.</div>
                    ) : (
                        feriados.map((f) => (
                            <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: `1px solid ${COR.bordaSuave}` }}>
                                <span style={{ width: '34px', flexShrink: 0, fontWeight: 700, color: CAMADA.feriado.cor, fontSize: '13px' }}>{String(f.dia).padStart(2, '0')}</span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 600, color: COR.texto, fontSize: '13px' }}>{f.nome}</div>
                                    <div style={{ color: '#999', fontSize: '11px' }}>
                                        {f.tipo}{f.municipio ? ` · ${f.municipio}` : ''}{f.uf ? `/${f.uf}` : ''}
                                    </div>
                                </div>
                                <button onClick={() => remover(f.id)} title="Remover" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', display: 'flex' }}><Trash2 size={16} /></button>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}

export default PainelFeriados;
